import type { Role } from "@hd2/data";
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "hd2:assignedClass";
const CHANGE_EVENT = "hd2:assignedClassChange";

export interface AssignedClass {
	slug: string;
	name: string;
	role: Role;
}

function read(): AssignedClass | null {
	const raw = localStorage.getItem(STORAGE_KEY);
	if (!raw) {
		return null;
	}
	try {
		return JSON.parse(raw) as AssignedClass;
	} catch {
		return null;
	}
}

export function useAssignedClass(): [
	AssignedClass | null,
	(next: AssignedClass | null) => void,
] {
	const [assigned, setAssigned] = useState<AssignedClass | null>(() => read());

	useEffect(() => {
		const sync = () => {
			setAssigned(read());
		};
		window.addEventListener("storage", sync);
		window.addEventListener(CHANGE_EVENT, sync);
		return () => {
			window.removeEventListener("storage", sync);
			window.removeEventListener(CHANGE_EVENT, sync);
		};
	}, []);

	const update = useCallback((next: AssignedClass | null) => {
		if (next) {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
		} else {
			localStorage.removeItem(STORAGE_KEY);
		}
		window.dispatchEvent(new Event(CHANGE_EVENT));
	}, []);

	return [assigned, update];
}
